import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowPathIcon, 
  ArrowUpTrayIcon,
  ArrowDownTrayIcon,
  ArrowLeftIcon,
} from '@heroicons/react/24/outline';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAppData } from '../../contexts/AppDataContext';
import toast from 'react-hot-toast';

// Import stock components
import StockEntryForm from '../../components/stock/StockEntryForm';
import StockExitForm from '../../components/stock/StockExitForm';
import StockAdjustmentForm from '../../components/stock/StockAdjustmentForm';
import StockValuationTab from '../../components/stock/StockValuationTab';
import StockAlertsTab from '../../components/stock/StockAlertsTab';
import StockThresholdTab from '../../components/stock/StockThresholdTab';
import StockTransfersTab from '../../components/stock/StockTransfersTab';
import StockAdjustmentsTab from '../../components/stock/StockAdjustmentsTab';
import StockTransferForm from '../../components/stock/StockTransferForm';
import StockMovementsTab from '../../components/stock/StockMovementsTab';

const StockOverview = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { 
    products, 
    stockItems,
    globalStats,
    fetchAllData
  } = useAppData();
  
  const [activeTab, setActiveTab] = useState('movements');
  
  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  const handleStockEntry = () => {
    navigate('/stock/entry');
  };

  const handleStockExit = () => {
    navigate('/stock/exit');
  };

  const handleStockAdjustment = () => {
    navigate('/stock/adjustment');
  };
  
  const handleOpenStockModal = (productId) => {
    console.log('Open stock modal for product:', productId);
  };

  const tabs = [
    { id: 'movements', name: 'Mouvements de stock' },
    { id: 'valuation', name: 'Valorisation' },
    { id: 'alerts', name: 'Alertes de stock' },
    { id: 'thresholds', name: 'Seuils & Alertes' },
    { id: 'transfers', name: 'Transferts' },
    { id: 'adjustments', name: 'Ajustements' }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-white">
            Gestion de Stock
          </h1>
          <p className="text-white/60 mt-2">
            Suivez les mouvements et l'état de votre inventaire
          </p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={handleStockEntry}
            className="bg-gradient-to-r from-green-600 to-emerald-600 text-white px-4 py-2.5 rounded-xl font-medium hover:from-green-700 hover:to-emerald-700 transition-all duration-300 flex items-center space-x-1"
          >
            <ArrowDownTrayIcon className="h-4 w-4" />
            <span>Entrée de stock</span>
          </button>
          <button
            onClick={handleStockExit}
            className="bg-gradient-to-r from-red-600 to-orange-600 text-white px-4 py-2.5 rounded-xl font-medium hover:from-red-700 hover:to-orange-700 transition-all duration-300 flex items-center space-x-1"
          >
            <ArrowUpTrayIcon className="h-4 w-4" />
            <span>Sortie de stock</span>
          </button>
          <button
            onClick={handleStockAdjustment}
            className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 py-2.5 rounded-xl font-medium hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 flex items-center space-x-1"
          >
            <ArrowPathIcon className="h-4 w-4" />
            <span>Ajustement</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
            className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
          >
            <div className="flex items-center justify-between mb-2">
              <div>
              <p className="text-white/60 text-sm font-medium">Total produits</p>
              <p className="text-2xl font-bold text-white mt-1">{products.length}</p>
              </div>
              </div>
          <p className="text-xs text-white/50">Produits en stock</p>
          </motion.div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
        <nav className="flex space-x-2">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-xl text-sm font-medium ${
                activeTab === tab.id
                  ? 'bg-white/10 text-white'
                  : 'text-white/60 hover:text-white hover:bg-white/5'
              }`}
            >
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'movements' && <StockMovementsTab />}

      {activeTab === 'valuation' && <StockValuationTab />}
      {activeTab === 'alerts' && <StockAlertsTab openStockModal={handleOpenStockModal} />}
      {activeTab === 'thresholds' && <StockThresholdTab />}
      {activeTab === 'transfers' && <StockTransfersTab />}
      {activeTab === 'adjustments' && <StockAdjustmentsTab />}
    </div>
  );
};

// Enhanced movement detail component
const StockMovementDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { stockItems, products, warehouses } = useAppData();
  const [loading, setLoading] = useState(true);
  const [movement, setMovement] = useState(null);
  
  // Find the movement details from stockItems
  useEffect(() => {
    if (stockItems && stockItems.length > 0) {
      const foundMovement = stockItems.find(item => item.id === id);
      setMovement(foundMovement || null);
      setLoading(false);
    }
  }, [id, stockItems]);
  
  // Helper functions
  const getProductName = (productId) => {
    if (!productId) return 'N/A';
    const product = products.find(p => p.id === productId);
    return product ? product.name : 'Produit inconnu';
  };
  
  const getWarehouseName = (warehouseId) => {
    if (!warehouseId) return 'Non spécifié';
    const warehouse = warehouses.find(w => String(w.id) === String(warehouseId));
    return warehouse ? warehouse.name : `Entrepôt #${warehouseId}`;
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  const getMovementTypeClass = (type) => {
    switch(type?.toLowerCase()) {
      case 'entrée': return 'bg-green-500/20 text-green-400';
      case 'sortie': return 'bg-red-500/20 text-red-400';
      case 'transfert': return 'bg-blue-500/20 text-blue-400';
      case 'ajustement': return 'bg-yellow-500/20 text-yellow-400';
      default: return 'bg-gray-500/20 text-gray-400';
    }
  };
  
  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/stock')}
            className="p-2 bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 text-white hover:bg-white/20 transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-white">Détails du mouvement</h1>
            <p className="text-white/60 mt-1">Chargement...</p>
          </div>
        </div>
        
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 flex justify-center items-center h-64">
          <ArrowPathIcon className="h-10 w-10 text-white/30 animate-spin" />
        </div>
      </div>
    );
  }
  
  if (!movement) {
  return (
    <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/stock')}
            className="p-2 bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 text-white hover:bg-white/20 transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
          <div>
          <h1 className="text-3xl font-bold text-white">Détails du mouvement</h1>
          <p className="text-white/60 mt-1">{id}</p>
        </div>
      </div>

      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
          <p className="text-white/70 text-center py-8">Mouvement non trouvé</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-4">
        <button
          onClick={() => navigate('/stock')}
          className="p-2 bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 text-white hover:bg-white/20 transition-colors"
        >
          <ArrowLeftIcon className="h-5 w-5" />
        </button>
        <div>
          <h1 className="text-3xl font-bold text-white">Détails du mouvement</h1>
          <p className="text-white/60 mt-1">{movement.id}</p>
        </div>
      </div>

      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
        {/* Movement type and date */}
        <div className="flex justify-between items-start mb-6">
          <div>
            <span className={`inline-flex items-center space-x-1.5 px-3 py-1.5 rounded-full text-sm font-medium ${getMovementTypeClass(movement.type)}`}>
              {movement.type || 'Inconnu'}
            </span>
          </div>
          <div className="text-right">
            <p className="text-white/60 text-sm">Date du mouvement</p>
            <p className="text-white font-medium">{formatDate(movement.date)}</p>
          </div>
        </div>
        
        {/* Product and quantity */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className="bg-white/5 rounded-xl p-4">
            <h3 className="text-white/60 text-sm mb-1">Produit</h3>
            <p className="text-white text-lg font-medium">{getProductName(movement.productId)}</p>
          </div>
          <div className="bg-white/5 rounded-xl p-4">
            <h3 className="text-white/60 text-sm mb-1">Quantité</h3>
            <p className="text-white text-lg font-medium">{movement.quantity}</p>
          </div>
        </div>
        
        {/* Warehouse and reference */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className="bg-white/5 rounded-xl p-4">
            <h3 className="text-white/60 text-sm mb-1">Entrepôt</h3>
            <p className="text-white">{getWarehouseName(movement.warehouse)}</p>
          </div>
          <div className="bg-white/5 rounded-xl p-4">
            <h3 className="text-white/60 text-sm mb-1">Référence</h3>
            <p className="text-white">{movement.reference || 'N/A'}</p>
          </div>
        </div>
        
        {/* Notes */}
        {movement.notes && (
          <div className="bg-white/5 rounded-xl p-4 mb-6">
            <h3 className="text-white/60 text-sm mb-1">Notes</h3>
            <p className="text-white">{movement.notes}</p>
          </div>
        )}
        
        {/* Additional info for transfers */}
        {movement.type === 'transfert' && movement.sourceWarehouse && movement.destinationWarehouse && (
          <div className="bg-white/5 rounded-xl p-4 mb-6">
            <h3 className="text-white/60 text-sm mb-2">Détails du transfert</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-white/60 text-xs">Entrepôt source</p>
                <p className="text-white">{getWarehouseName(movement.sourceWarehouse)}</p>
              </div>
              <div>
                <p className="text-white/60 text-xs">Entrepôt destination</p>
                <p className="text-white">{getWarehouseName(movement.destinationWarehouse)}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Simple movement edit component
const StockMovementEdit = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  
  return (
    <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/stock')}
            className="p-2 bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 text-white hover:bg-white/20 transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
          <div>
          <h1 className="text-3xl font-bold text-white">Modifier le mouvement</h1>
          <p className="text-white/60 mt-1">{id}</p>
        </div>
      </div>

        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
        <p className="text-white">Édition du mouvement de stock ID: {id}</p>
            </div>
    </div>
  );
};

const StockPage = () => {
  return (
    <Routes>
      <Route path="/" element={<StockOverview />} />
      <Route path="/entry" element={<StockEntryForm />} />
      <Route path="/exit" element={<StockExitForm />} />
      <Route path="/adjustment" element={<StockAdjustmentForm />} />
      <Route path="/transfer" element={<StockTransferForm />} />
      <Route path="/movement/:id" element={<StockMovementDetail />} />
      <Route path="/movement/:id/edit" element={<StockMovementEdit />} />
    </Routes>
  );
};

export default StockPage;